import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect, useState } from 'react'

import { useWindowLocation } from 'utils/windowLocation'

import { getTypeForCensusStatus } from './ProspectCensusDetails'
import { type ProspectEmployeeCensus } from './types'
import { getBrowserTimezoneDatetimeString } from '../../utils'
import { useGetCensuses } from '../hooks/useGetCensuses'
import Badge from '../reusable/Badge'
import TanstackTable, {
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<ProspectEmployeeCensus>()
const columns = [
  columnHelper.accessor('file_name', {
    header: 'Census name',
  }),
  columnHelper.accessor('status', {
    header: 'Parsing status',
    cell: (info) => (
      <Badge
        type={getTypeForCensusStatus(info.getValue())}
        text={info.getValue()}
      />
    ),
  }),
  columnHelper.accessor('created_at', {
    header: 'Date and time created',
    cell: (info) =>
      info.getValue()
        ? getBrowserTimezoneDatetimeString(new Date(info.getValue()))
        : undefined,
  }),
]

const PAGE_SIZE = 10

export function ProspectEmployeeCensusList ({
  prospectId,
  isForAdminView = false,
}: {
  pageParam?: number | null;
  prospectId: string;
  isForAdminView?: boolean;
}) {
  const { assign } = useWindowLocation()

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetCensuses({
    prospectId,
    pagination,
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no censuses for this prospect.'
      onRowClick={({ row }) => {
        const recordId = row.original.id
        assign(`/${
          isForAdminView ? 'admin' : 'broker'
        }/prospects/${prospectId}/census/${recordId}`)
      }}
      paginationLabelSingular='census'
      paginationLabelPlural='censuses'
      paginationMetadata={paginationMetadata}
      onNextPageClick={() => {
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })
      }}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}
